/**
 * "Coming soon" — a menu entry that is honest about not being built yet.
 *
 * Listing an unbuilt feature is a promise, so the dialog names what the thing
 * will be rather than only that it is coming: a trainer who taps My Pokémon
 * deserves to know whether it is worth waiting for, and "coming soon" on its
 * own tells them nothing.
 *
 * ONE LINE, deliberately. A bulleted spec for something unbuilt reads as a
 * commitment to each bullet, and the honest version of an unfinished feature is
 * a headline, not a roadmap (user direction, 2026-08-14).
 *
 * The entries carry a visible "Soon" badge in the menu itself, so nobody taps
 * one expecting a page: the dialog confirms rather than surprises.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function ComingSoonDialog({
  open,
  onOpenChange,
  title,
  blurb,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The whole promise, in one line. */
  blurb: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="coming-soon">
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            {title}
            <span
              className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={{ background: "var(--surface-sunken)", color: "var(--text-tertiary)" }}
            >
              Soon
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">{blurb}</DialogDescription>
        </DialogHeader>
        <button
          type="button"
          data-testid="coming-soon-close"
          onClick={() => onOpenChange(false)}
          className="w-full rounded-full py-2.5 text-[12px] font-bold"
          style={{
            background: "var(--surface-sunken)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-subtle)",
            minHeight: 44,
          }}
        >
          Got it
        </button>
      </DialogContent>
    </Dialog>
  );
}
